import clsx from "clsx";
import React from "react";
import { useTranslation } from "react-i18next";
import useFetchInterval from "src/ui/hooks/useFetchInterval";

import { useFetchProductBuildsQuery } from "@dashboard/products/api";
import { Build } from "@dashboard/products/types";
import { Product } from "@dashboard/products/types/index";
import { Typography } from "@mui/material";
import SemverService from "@services/semverService/semverService";
import { SectionPanel } from "@shared/SectionPanel/SectionPanel";
import {
    MinimalTable,
    MinimalTableCell,
    MinimalTableContainer,
    MinimalTableHeader,
    MinimalTableRow,
} from "@shared/table/MinimalTableComponents";
import { TableBody } from "@shared/table/TableBody";
import { TableHeaderRows } from "@shared/table/TableHeader";
import { TableRowCells } from "@shared/table/TableRowCells";
import {
    createColumnHelper,
    functionalUpdate,
    getCoreRowModel,
    getSortedRowModel,
    SortingState,
    Updater,
    useReactTable,
} from "@tanstack/react-table";

import { useBuildsTableStyles } from "./BuildsTable.style";
import { ArtifactsCell, BuildActionsCell, ChannelCell, DownloadsCell, StatusCell, TagsCell, TimestampCell } from "./Cells";
import { getFirmwareDownloads } from "./firmwareDownloads";
import { isBuildProcessing } from "./isBuildProcessing";
import { useBuildStatusPulse } from "./useBuildStatusPulse";

const columnHelper = createColumnHelper<Build>();

type ProductProps = {
    product: Product;
};
const BuildsTable = (props: ProductProps) => {
    const [sorting, setSorting] = React.useState<SortingState>([
        { id: "version", desc: true },
        { id: "created", desc: true },
    ]);

    const { t } = useTranslation(["common", "pages"]);
    const { classes } = useBuildsTableStyles();

    const { data = [], isLoading, isError, refetch } = useFetchProductBuildsQuery(props.product.id);

    const hasProcessingBuilds = data.some(isBuildProcessing);

    useFetchInterval(refetch, { condition: hasProcessingBuilds });

    const { isPulsing, onPulseEnd } = useBuildStatusPulse(data);

    const onSortingChange = (updater: Updater<SortingState>) => {
        const newValue = functionalUpdate(updater, sorting);
        setSorting(newValue);
    };

    const tableColumns = React.useMemo(
        () => [
            columnHelper.accessor("buildStatus", {
                header: t("common:label.status"),
                cell: (info) => (
                    <StatusCell buildStatus={info.getValue()} build={info.row.original} productId={props.product.id} />
                ),
                enableSorting: true,
                meta: { align: "center" },
            }),
            columnHelper.accessor("version", {
                header: t("common:label.version"),
                cell: (info) => (
                    <MinimalTableCell width={100}>
                        <Typography>{info.getValue()}</Typography>
                    </MinimalTableCell>
                ),
                enableSorting: true,
                sortingFn: (rowA, rowB) => SemverService.compare(rowA.original.version, rowB.original.version),
            }),
            columnHelper.accessor("created", {
                header: t("common:label.date"),
                cell: (info) => <TimestampCell created={info.getValue()} finished={info.row.original.built} />,
                enableSorting: true,
            }),
            columnHelper.accessor("artifacts", {
                header: t("pages:productPage.buildsTable.column.artifacts"),
                cell: (info) => <ArtifactsCell build={info.row.original} />,
                enableSorting: false,
            }),
            columnHelper.accessor("metadata", {
                id: "tags",
                header: t("common:label.tags"),
                cell: (info) => <TagsCell build={info.row.original} />,
                enableSorting: false,
            }),
            columnHelper.accessor("channel", {
                header: t("common:label.channel"),
                cell: (info) => <ChannelCell build={info.row.original} />,
            }),
            columnHelper.accessor(getFirmwareDownloads, {
                id: "downloads",
                header: t("pages:productPage.buildsTable.column.downloads"),
                cell: (info) => <DownloadsCell build={info.row.original} />,
                enableSorting: true,
            }),
            columnHelper.display({
                id: "actions",
                header: "",
                cell: (info) => <BuildActionsCell build={info.row.original} product={props.product} />,
                meta: { align: "right" },
            }),
        ],
        [props.product, t],
    );

    const table = useReactTable<Build>({
        data,
        columns: tableColumns,
        state: { sorting },
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        onSortingChange: onSortingChange,
    });

    return (
        <SectionPanel disablePadding className={classes.panel}>
            <MinimalTableContainer plain>
                <MinimalTable stickyHeader>
                    <MinimalTableHeader
                        title={{
                            label: t("pages:productPage.listOfBuilds"),
                            colSpan: table.getVisibleLeafColumns().length,
                        }}
                    >
                        <TableHeaderRows headerGroups={table.getHeaderGroups()} />
                    </MinimalTableHeader>
                    <TableBody
                        table={table}
                        loading={isLoading}
                        noDataMessage={t("pages:productPage.clickBuildToCreateNewRelease")}
                        error={{ isError, onError: refetch }}
                    >
                        {table.getRowModel().rows.map((row) => (
                            <MinimalTableRow
                                key={row.id}
                                className={clsx(isPulsing(row.original.buildId) && classes.pulseRow)}
                                onAnimationEnd={(e) => {
                                    // ignore bubbling animationend from any nested element
                                    if (e.target === e.currentTarget) {
                                        onPulseEnd(row.original.buildId);
                                    }
                                }}
                            >
                                <TableRowCells row={row} />
                            </MinimalTableRow>
                        ))}
                    </TableBody>
                </MinimalTable>
            </MinimalTableContainer>
        </SectionPanel>
    );
};

export default BuildsTable;
